import { supabase } from '@/lib/supabase'
import type { IsoDate } from '@/lib/dates'

/**
 * The couple's portal talks only to the client-portal edge function: the
 * private link's token is the key (supabase/functions/client-portal).
 */

export interface PortalLine {
  description: string
  detail: string | null
  line_total: number | string
  sort_order: number
}

export interface PortalQuote {
  id: string
  quote_number: number
  status: 'sent' | 'viewed' | 'accepted' | 'declined' | 'expired'
  valid_until: IsoDate | null
  subtotal: number | string
  vat: number | string
  total: number | string
  cover_note: string | null
  sent_at: string | null
  viewed_at: string | null
  accepted_at: string | null
  created_at: string
  line_items: PortalLine[]
}

export interface PortalInvoice {
  id: string
  invoice_number: number
  kind: 'booking' | 'deposit' | 'balance' | 'additional' | 'standard'
  status: 'sent' | 'partially_paid' | 'paid'
  issue_date: IsoDate
  due_date: IsoDate
  subtotal: number | string
  vat: number | string
  total: number | string
  amount_paid: number | string
  note: string | null
  sent_at: string | null
  viewed_at: string | null
  paid_at: string | null
  line_items: PortalLine[]
  instalments: Array<{ label: string; amount: number | string; due_date: IsoDate; position: number }>
}

export interface PortalFile {
  id: string
  name: string
  content_type: string | null
  size_bytes: number | null
  added_by: 'studio' | 'couple'
  uploaded_at: string
  /** A quote, invoice, contract or questionnaire uploaded as a PDF; null for a plain file. */
  slot?: 'quote' | 'invoice' | 'contract' | 'questionnaire' | null
}

export interface Portal {
  business: {
    name: string
    email: string | null
    phone: string | null
    website: string | null
    vatRegistered: boolean
    quotePrefix: string | null
    invoicePrefix: string | null
    bank: {
      accountName: string | null
      bankName: string | null
      branchCode: string | null
      accountNumber: string | null
      accountType: string | null
    }
  } | null
  project: {
    kind: string
    title: string | null
    eventDate: IsoDate
    venue: string | null
    package: string | null
    contractSignedAt: string | null
    people: Array<{ firstName: string; lastName: string | null }>
  }
  quotes: PortalQuote[]
  invoices: PortalInvoice[]
  files: PortalFile[]
  deliverables: Array<{ title: string; kind: string; delivered: boolean }>
  /** Questionnaires Jena has sent them, each with its own /q/<token> link (29 Oct 2026). */
  questionnaires?: Array<{ name: string; token: string; completedAt: string | null }>
}

export async function callPortal<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error, response } = await supabase.functions.invoke<T & { error?: string }>('client-portal', { body })
  if (!error && data) return data
  const res = (error as { context?: Response } | null)?.context ?? response
  const parsed = (await res?.json().catch(() => null)) as { error?: string } | null
  throw new Error(parsed?.error ?? 'Something went wrong. Please try again, or tell Jenalee.')
}

/** Uploads one of the couple's files: a signed upload slot, the upload, then a confirmation. */
export async function uploadToPortal(token: string, file: File, preview: boolean): Promise<void> {
  const slot = await callPortal<{ fileId: string; path: string; uploadToken: string }>({
    token,
    preview,
    upload: { name: file.name, type: file.type, size: file.size },
  })
  const { error } = await supabase.storage.from('documents').uploadToSignedUrl(slot.path, slot.uploadToken, file, {
    contentType: file.type || 'application/octet-stream',
  })
  if (error) throw new Error('The upload did not go through. Please try again.')
  await callPortal({ token, preview, uploaded: slot.fileId })
}
